import Dialog, { DialogProps } from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import React from 'react'
import { observer } from 'mobx-react-lite'
import { GapDialogStore, GapDialogStoreProvider } from './GapDialogStore'
import useStore from 'reusable/mobx-store-utils'
import ModalState from 'reusable/mobx-helpers/ModalState'
import GapDialogTitle from './GapDialogTitle'
import GapDialogTabs, { GapDialogTab } from './GapDialogTabs'
import GapDialogSidebarWrapper from './GapDialogSidebarWrapper'
import GapDialogActionButtons from './GapDialogActionButtons'
import { GapDialogAction } from './GapDialogActionButton'
import GapChildSlideAnimation from 'reusable/transitions/GapChildSlideAnimation'
import { DialogSize } from '@gap/shared/theme'
import useMatrix from 'reusable/goose/Matrix'

export type GapDialogProps = {
   /** Used in quick actions like yes-no dialogs */
   resolve?: (value: GapDialogAction | PromiseLike<GapDialogAction>) => void
   /** Callback for when clicking outside of this dialog */
   clickAwayAction?: { name: string, callback: () => void }
   /** Extra props to apply directly on the Dialog */
   DialogProps?: Omit<DialogProps, 'title' | 'open' | 'onClose' | 'container' | 'children' | 'resolve' | 'PaperProps'>
   /** pass through the entire modalstate */
   modalState?: ModalState
   children?: React.ReactNode
   noPadding?: boolean

   /** Button definitions */
   primary?: GapDialogAction
   secondary?: GapDialogAction
   cancel?: GapDialogAction | boolean

   title?: React.ReactElement | string
   subTitle?: React.ReactElement | string
   sidebar?: React.ReactNode
   size?: DialogSize
   tabs?: GapDialogTab[]
   /** The tab shown on open and again after each close; the first tab when omitted. */
   initialTabIndex?: number
   /** Show a divider between the title and the content */
   showDivider?: boolean

   /** @deprecated Please pass ModalState and use that */
   open?: boolean
}

export default observer(function GapDialog(props: GapDialogProps) {
   const gapDialogStore = useStore(sp => new GapDialogStore(sp), props)
   const matrix = useMatrix()

   return <GapDialogStoreProvider store={gapDialogStore}>
      <Dialog
         {...gapDialogStore.dialogProps}
         open={gapDialogStore.open}
         onClose={gapDialogStore.handleDirectClose}
         data-testid="popup-dialog"
         {...matrix.props}
      >
         <GapDialogSidebarWrapper sidebar={props.sidebar}>
            <GapDialogTitle />

            <DialogContent sx={{ position: 'relative', overflow: 'auto', minHeight: 100, p: props.noPadding ? 0 : undefined }}>
               <GapChildSlideAnimation
                  mainComponent={<>
                     <GapDialogTabs />
                     {gapDialogStore.mainContent}
                  </>}
                  childComponent={gapDialogStore.childContent}
               />
            </DialogContent>

            {gapDialogStore.dialogHasActions && <GapDialogActionButtons />}
         </GapDialogSidebarWrapper>
      </Dialog>
   </GapDialogStoreProvider>
})
